// --- src/components/checkout/OrderPanel.jsx ---
import React, { useEffect, useState } from "react";
import { CreditCardIcon, HomeIcon, QrCodeIcon, XMarkIcon } from "@heroicons/react/24/outline";
import RadioInput from "../common/RadioInput";
import { getDeliverySlots, applyCoupon, applyGiftCard, searchProducts } from "../../api/productApi";

export default function OrderPanel({
    isNewMember,
    memberBaseCredits,
    issueCredits,
    returnCredits,
    finalAvailableCredits,
    damageFees,
    totalAmount,
    onShippingChange,
    onPaymentChange,
    onCheckout,
    orderType = "WALK_IN",
    onOrderTypeChange = () => { },
    deliverySlot = "",
    onDeliverySlotChange = () => { },
    token,
    pincode,
    shelfItems = [],
    onAddScannedBook = () => { },
    checkoutLoading = false
}) {

    const creditSectionClass = isNewMember ? "hidden" : "";
    const shippingSectionClass = isNewMember ? "hidden" : "";

    // --- LOGIC UPDATE ---
    const paymentSectionClass = "";
    // --- End Update ---

    const [availableSlots, setAvailableSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);

    // Note state
    const [note, setNote] = useState("");

    // Coupon states
    const [couponCodeInput, setCouponCodeInput] = useState("");
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [couponLoading, setCouponLoading] = useState(false);
    const [couponError, setCouponError] = useState("");

    // Gift Card states
    const [giftCardInput, setGiftCardInput] = useState("");
    const [appliedGiftCard, setAppliedGiftCard] = useState(null);
    const [giftCardLoading, setGiftCardLoading] = useState(false);
    const [giftCardError, setGiftCardError] = useState("");

    // Scanner states
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const [scanQuery, setScanQuery] = useState("");
    const [scanLoading, setScanLoading] = useState(false);
    const [scanError, setScanError] = useState("");
    const [scannedList, setScannedList] = useState([]);

    const handleScanSearch = async (queryVal) => {
        const query = queryVal.trim();
        if (!query) return;

        setScanLoading(true);
        setScanError("");
        try {
            // First try searching by copy_id
            let res = await searchProducts({
                token,
                copy_id: query,
                page: 1,
                perPage: 1,
            });

            // If not found by copy_id, try smart_product_id (numeric) or generic query
            if (!res?.data || res.data.length === 0) {
                if (/^\d+$/.test(query)) {
                    res = await searchProducts({
                        token,
                        smart_product_id: query,
                        page: 1,
                        perPage: 1,
                    });
                } else {
                    res = await searchProducts({
                        token,
                        q: query,
                        page: 1,
                        perPage: 1,
                    });
                }
            }

            if (res?.data && res.data.length > 0) {
                const rawProduct = res.data[0];
                const book = {
                    id: rawProduct.id,
                    title: rawProduct.name,
                    img: rawProduct.image || "https://via.placeholder.com/120x180",
                    credits: rawProduct.price ?? 0,
                    stock_status: rawProduct.stock_status ?? "Available",
                    smart_product_id: rawProduct.smart_product_id,
                    copyId: query,
                };

                // Check if book or copy is on member's shelf
                const isAlreadyIssued = shelfItems.some(item =>
                    String(item.copyId) === String(query) ||
                    String(item.id) === String(book.id)
                );

                const status = isAlreadyIssued ? "Returning" : "Issuing";

                if (status === "Issuing" && book.stock_status === "outofstock") {
                    setScanError("Cannot add out of stock books to cart.");
                    return;
                }

                onAddScannedBook(book, status);

                setScannedList(prev => {
                    if (prev.some(x => x.id === book.id)) return prev;
                    return [{ ...book, status }, ...prev];
                });

                setScanQuery("");
            } else {
                setScanError("No book found matching this code.");
            }
        } catch (e) {
            console.error("Scan search failed", e);
            setScanError("Error searching code. Please try again.");
        } finally {
            setScanLoading(false);
        }
    };

    const handleApplyCoupon = async () => {
        setCouponError("");
        if (!couponCodeInput.trim()) {
            setCouponError("Please enter a coupon code");
            return;
        }

        if (appliedCoupon) {
            setCouponError("A coupon is already applied. Please remove it first.");
            return;
        }

        setCouponLoading(true);
        try {
            const res = await applyCoupon({ token, couponCode: couponCodeInput.trim() });
            if (res && res.success) {
                setAppliedCoupon(res);
                setCouponCodeInput("");
            } else {
                setCouponError(res?.message || "Invalid coupon code");
            }
        } catch (e) {
            setCouponError(e.message || "Failed to apply coupon");
        } finally {
            setCouponLoading(false);
        }
    };

    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setCouponError("");
    };

    const handleApplyGiftCard = async () => {
        setGiftCardError("");
        if (!giftCardInput.trim()) {
            setGiftCardError("Please enter a gift card code");
            return;
        }

        if (appliedGiftCard) {
            setGiftCardError("A gift card is already applied. Please remove it first.");
            return;
        }

        setGiftCardLoading(true);
        try {
            const res = await applyGiftCard({ token, giftCardCode: giftCardInput.trim() });
            if (res && res.success) {
                const cardData = res.data;
                if (cardData) {
                    setAppliedGiftCard(cardData);
                    setGiftCardInput("");
                } else {
                    setGiftCardError("Could not retrieve gift card data");
                }
            } else {
                setGiftCardError(res?.message || "Invalid gift card code");
            }
        } catch (e) {
            setGiftCardError(e.message || "Failed to apply gift card");
        } finally {
            setGiftCardLoading(false);
        }
    };

    const handleRemoveGiftCard = () => {
        setAppliedGiftCard(null);
        setGiftCardError("");
    };

    // Delivery charge (currently 0 as no API exists yet)
    const deliveryCharge = 0;
    const cartTotal = totalAmount;

    // Calculate coupon discount
    let couponDiscount = 0;
    let shippingDiscount = 0;

    if (appliedCoupon) {
        const type = appliedCoupon.discount_type || appliedCoupon.type || "fixed_cart";
        const val = parseFloat(appliedCoupon.discount_amount || appliedCoupon.amount || 0);
        const freeShipping = appliedCoupon.free_shipping === true || appliedCoupon.free_shipping === "yes" || appliedCoupon.free_shipping === 1 || appliedCoupon.free_shipping === "1";

        if (type === "percent") {
            couponDiscount = (totalAmount * val) / 100;
        } else if (type === "fixed_cart" || type === "fixed_product") {
            couponDiscount = val;
        } else {
            couponDiscount = val;
        }

        if (freeShipping && orderType === "DELIVERY") {
            shippingDiscount = deliveryCharge; // free shipping waives delivery charges
        }
    }

    const totalAfterCoupon = Math.max(0, totalAmount - couponDiscount - shippingDiscount);

    // Calculate Gift Card discount
    let giftCardDiscount = 0;
    let remainingGiftCardBalance = 0;

    if (appliedGiftCard) {
        const giftCardBalance = parseFloat(appliedGiftCard.balance || 0);
        if (giftCardBalance >= totalAfterCoupon) {
            giftCardDiscount = totalAfterCoupon;
            remainingGiftCardBalance = giftCardBalance - totalAfterCoupon;
        } else {
            giftCardDiscount = giftCardBalance;
            remainingGiftCardBalance = 0;
        }
    }

    const finalTotal = Math.max(0, totalAfterCoupon - giftCardDiscount);

    console.log("Order Type", orderType);
    console.log("Cart Total", cartTotal);
    console.log("Delivery Charge", deliveryCharge);
    console.log("Final Total", finalTotal);

    useEffect(() => {
        if (orderType !== "DELIVERY") {
            setAvailableSlots([]);
            return;
        }

        let ignore = false;
        async function loadSlots() {
            if (!pincode) {
                setAvailableSlots([]);
                return;
            }
            try {
                setSlotsLoading(true);
                const res = await getDeliverySlots({ token, pincode });
                if (!ignore) {
                    if (res && res.success && Array.isArray(res.data)) {
                        setAvailableSlots(res.data);
                    } else {
                        setAvailableSlots([]);
                    }
                }
            } catch (e) {
                console.error("Failed to load slots", e);
                if (!ignore) {
                    setAvailableSlots([]);
                }
            } finally {
                if (!ignore) {
                    setSlotsLoading(false);
                }
            }
        }
        loadSlots();

        return () => {
            ignore = true;
        };
    }, [orderType, token, pincode]);

    return (
        <div className="bg-white p-6 rounded-lg shadow space-y-5">
            <div className="space-y-2">
                <label className="block text-md font-semibold text-gray-700">Coupon Code:</label>
                <div className="flex gap-2">
                    <input
                        type="text"
                        placeholder="Enter Coupon Code"
                        value={couponCodeInput}
                        onChange={(e) => setCouponCodeInput(e.target.value)}
                        disabled={couponLoading}
                        className="w-full border border-gray-300 rounded-md p-2 text-sm outline-none focus:border-black"
                    />
                    <button
                        type="button"
                        onClick={handleApplyCoupon}
                        disabled={couponLoading}
                        className="bg-black text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-800 disabled:bg-gray-400 transition-colors"
                    >
                        {couponLoading ? "Applying..." : "Apply"}
                    </button>
                </div>
                {couponError && <p className="text-red-500 text-xs mt-1">⚠️ {couponError}</p>}
            </div>

            {appliedCoupon && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs space-y-1 relative animate-fade-in">
                    <div className="font-bold text-gray-800">Applied Coupon:</div>
                    <div className="text-gray-900 font-semibold text-sm">{appliedCoupon.coupon_code || appliedCoupon.code}</div>
                    <div className="text-gray-600 font-medium">
                        ₹{appliedCoupon.discount_amount || appliedCoupon.amount} Discount ({appliedCoupon.discount_type || appliedCoupon.type})
                    </div>
                    {appliedCoupon.free_shipping ? (
                        <div className="text-green-600 font-semibold mt-1">✓ Free Shipping Enabled</div>
                    ) : null}
                    <button
                        type="button"
                        onClick={handleRemoveCoupon}
                        className="absolute top-2 right-2 text-xs text-red-500 hover:text-red-700 font-semibold hover:underline"
                    >
                        Remove
                    </button>
                </div>
            )}

            {/* Gift Card section */}
            <div className="space-y-2 border-t pt-4">
                <label className="block text-md font-semibold text-gray-700">Gift Card:</label>
                <div className="flex gap-2">
                    <input
                        type="text"
                        placeholder="Enter Gift Card"
                        value={giftCardInput}
                        onChange={(e) => setGiftCardInput(e.target.value)}
                        disabled={giftCardLoading}
                        className="w-full border border-gray-300 rounded-md p-2 text-sm outline-none focus:border-black"
                    />
                    <button
                        type="button"
                        onClick={handleApplyGiftCard}
                        disabled={giftCardLoading}
                        className="bg-black text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-800 disabled:bg-gray-400 transition-colors"
                    >
                        {giftCardLoading ? "Applying..." : "Apply"}
                    </button>
                </div>
                {giftCardError && <p className="text-red-500 text-xs mt-1">⚠️ {giftCardError}</p>}
            </div>

            {appliedGiftCard && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs space-y-1 relative animate-fade-in">
                    <div className="font-bold text-blue-800">Applied Gift Card:</div>
                    <div className="text-blue-900 font-semibold text-sm">{appliedGiftCard.code}</div>
                    <div className="text-blue-700 font-medium">Description: {appliedGiftCard.description || "Gift Card"}</div>
                    <div className="text-blue-650 font-medium">Available Balance: ₹{parseFloat(appliedGiftCard.balance || 0).toFixed(2)}</div>
                    <button
                        type="button"
                        onClick={handleRemoveGiftCard}
                        className="absolute top-2 right-2 text-xs text-red-500 hover:text-red-700 font-semibold hover:underline"
                    >
                        Remove
                    </button>
                </div>
            )}

            <div className={`border-t pt-4 space-y-2 text-sm ${creditSectionClass}`}>
                <div className="flex justify-between">
                    <span className="text-gray-600">Member's Current Credits</span>
                    <span className="font-medium text-gray-800">{memberBaseCredits} credits</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-600">Issue Credits</span>
                    <span className="font-medium text-red-600"> - {issueCredits} credits</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-600">Return Credits</span>
                    <span className="font-medium text-green-600"> + {returnCredits} credits</span>
                </div>
                {/* --- LOGIC UPDATE --- */}
                <div className="flex justify-between">
                    <span className="text-gray-600">Damage Fees</span>
                    <span className="font-medium text-red-600">₹ {finalTotal.toFixed(2)}</span>
                </div>
                {/* --- End Update --- */}
                <div className="flex justify-between text-base font-semibold border-t pt-2 mt-2">
                    <span className="text-gray-800">Final Available Credits</span>
                    <span className={finalAvailableCredits < 0 ? "text-red-600" : "text-green-600"}>{finalAvailableCredits} credits</span>
                </div>
            </div>

            <div className={`border-t pt-4 ${shippingSectionClass}`}>
                <h3 className="text-base font-semibold mb-3">Order Type</h3>
                <div className="grid grid-cols-3 gap-2">
                    <button
                        type="button"
                        onClick={() => onOrderTypeChange("WALK_IN")}
                        className={`flex flex-col items-center justify-center p-3 rounded-lg border text-center transition-all ${orderType === "WALK_IN"
                            ? "border-black bg-black text-white shadow-sm"
                            : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                            }`}
                    >
                        <HomeIcon className={`h-5 w-5 mb-1 ${orderType === "WALK_IN" ? "text-white" : "text-gray-500"}`} />
                        <span className="text-xs font-semibold">Walk In</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => onOrderTypeChange("VIRTUAL")}
                        className={`flex flex-col items-center justify-center p-3 rounded-lg border text-center transition-all ${orderType === "VIRTUAL"
                            ? "border-black bg-black text-white shadow-sm"
                            : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                            }`}
                    >
                        <svg
                            className={`h-5 w-5 mb-1 ${orderType === "VIRTUAL" ? "text-white" : "text-gray-500"}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span className="text-xs font-semibold">Virtual Pick-Up</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => onOrderTypeChange("DELIVERY")}
                        className={`flex flex-col items-center justify-center p-3 rounded-lg border text-center transition-all ${orderType === "DELIVERY"
                            ? "border-black bg-black text-white shadow-sm"
                            : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                            }`}
                    >
                        <CreditCardIcon className={`h-5 w-5 mb-1 ${orderType === "DELIVERY" ? "text-white" : "text-gray-500"}`} />
                        <span className="text-xs font-semibold">Delivery</span>
                    </button>
                </div>

                {orderType === "DELIVERY" && (
                    <div className="mt-4 flex flex-col gap-1.5 animate-fade-in">
                        <label className="text-xs font-semibold text-gray-700 flex items-center gap-2">
                            <span>Delivery Slot</span>
                            {slotsLoading && (
                                <span className="text-[10px] text-gray-400 animate-pulse">(Loading slots...)</span>
                            )}
                        </label>
                        <select
                            value={deliverySlot ? (typeof deliverySlot === 'object' ? JSON.stringify(deliverySlot) : deliverySlot) : ""}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (!val) {
                                    onDeliverySlotChange("");
                                } else {
                                    try {
                                        onDeliverySlotChange(JSON.parse(val));
                                    } catch (err) {
                                        onDeliverySlotChange(val);
                                    }
                                }
                            }}
                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white w-full outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-300 transition-colors shadow-sm text-gray-800"
                        >
                            {availableSlots.length === 0 ? (
                                <option value="">No delivery slots available</option>
                            ) : (
                                <>
                                    <option value="">Select Delivery Slot</option>
                                    {availableSlots.map((slot, index) => {
                                        const dateStr = slot.date;
                                        let formattedDate = dateStr;
                                        try {
                                            const parts = dateStr.split("-");
                                            if (parts.length === 3) {
                                                const dateObj = new Date(parts[0], parts[1] - 1, parts[2]);
                                                formattedDate = dateObj.toLocaleDateString("en-GB", {
                                                    day: "numeric",
                                                    month: "short",
                                                    year: "numeric"
                                                });
                                            }
                                        } catch (e) { }

                                        const formatTime = (time24) => {
                                            if (!time24) return "";
                                            const [hoursStr, minutesStr] = time24.split(":");
                                            const hours = parseInt(hoursStr, 10);
                                            const ampm = hours >= 12 ? "PM" : "AM";
                                            const hours12 = hours % 12 || 12;
                                            return `${hours12}:${minutesStr} ${ampm}`;
                                        };

                                        const startTime = formatTime(slot.start);
                                        const endTime = formatTime(slot.end);

                                        const label = `${formattedDate} | ${startTime} - ${endTime} | Zone ${slot.zone || ""} | Capacity: ${slot.capacity || ""}`;

                                        return (
                                            <option key={index} value={JSON.stringify(slot)}>
                                                {label}
                                            </option>
                                        );
                                    })}
                                </>
                            )}
                        </select>

                        {deliverySlot && typeof deliverySlot === 'object' && (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs space-y-1 mt-2 animate-fade-in">
                                <div className="font-bold text-gray-800">Selected Delivery Slot:</div>
                                <div className="text-gray-900 font-semibold">{(() => {
                                    const dateStr = deliverySlot.date;
                                    try {
                                        const parts = dateStr.split("-");
                                        if (parts.length === 3) {
                                            const dateObj = new Date(parts[0], parts[1] - 1, parts[2]);
                                            return dateObj.toLocaleDateString("en-GB", {
                                                day: "numeric",
                                                month: "short",
                                                year: "numeric"
                                            });
                                        }
                                    } catch (e) { }
                                    return dateStr;
                                })()}</div>
                                <div className="text-gray-700 font-medium">{(() => {
                                    const formatTime = (time24) => {
                                        if (!time24) return "";
                                        const [hoursStr, minutesStr] = time24.split(":");
                                        const hours = parseInt(hoursStr, 10);
                                        const ampm = hours >= 12 ? "PM" : "AM";
                                        const hours12 = hours % 12 || 12;
                                        return `${hours12}:${minutesStr} ${ampm}`;
                                    };
                                    return `${formatTime(deliverySlot.start)} - ${formatTime(deliverySlot.end)}`;
                                })()}</div>
                                <div className="text-gray-600">Zone {deliverySlot.zone}</div>
                                <div className="text-gray-500">Capacity: {deliverySlot.capacity}</div>
                            </div>
                        )}
                    </div>
                )}

                {orderType === "WALK_IN" && (
                    <div className="mt-4 flex flex-col gap-1.5 animate-fade-in">
                        <label className="text-xs font-semibold text-gray-700 flex items-center gap-2">
                            <span>Book Scanner</span>
                        </label>
                        <button
                            type="button"
                            onClick={() => {
                                setIsScannerOpen(true);
                                setScanError("");
                                setScanQuery("");
                            }}
                            className="w-full flex items-center justify-center gap-2 bg-gray-50 border border-gray-200 hover:bg-gray-100 text-gray-700 hover:text-black font-semibold text-sm px-4 py-2.5 rounded-lg transition-all shadow-sm"
                        >
                            <QrCodeIcon className="h-4 w-4 text-gray-500" />
                            <span>Book Scanner / Search Book</span>
                        </button>
                    </div>
                )}
            </div>

            {/* --- LOGIC UPDATE --- */}
            <div className={`border-t pt-4 ${paymentSectionClass}`}>
                <h3 className="text-base font-semibold mb-3">Payment</h3>
                <div className="space-y-3" onChange={(e) => onPaymentChange(e.target.value)}>
                    <RadioInput name="payment" value="AtStore" label="Pay at Store" description="Pay at store with cash or any other payment method" />
                    <RadioInput name="payment" value="Online" label="Online Payment" description="Our personal data will be used to process your order" />
                </div>
            </div>

            <div className="border-t pt-4 space-y-3">
                <div className="space-y-1.5 text-sm">
                    {(appliedCoupon || appliedGiftCard) && (
                        <div className="flex justify-between text-gray-600">
                            <span>Subtotal</span>
                            <span>₹ {totalAmount.toFixed(2)}</span>
                        </div>
                    )}
                    {appliedCoupon && (
                        <>
                            <div className="flex justify-between text-red-650 font-medium">
                                <span>Coupon Discount ({appliedCoupon.coupon_code || appliedCoupon.code})</span>
                                <span>-₹ {couponDiscount.toFixed(2)}</span>
                            </div>
                            {shippingDiscount > 0 && (
                                <div className="flex justify-between text-green-650 font-medium">
                                    <span>Free Shipping Discount</span>
                                    <span>-₹ {shippingDiscount.toFixed(2)}</span>
                                </div>
                            )}
                        </>
                    )}
                    {appliedGiftCard && (
                        <>
                            <div className="flex justify-between text-red-650 font-medium">
                                <span>Gift Card Discount ({appliedGiftCard.code})</span>
                                <span>-₹ {giftCardDiscount.toFixed(2)}</span>
                            </div>
                            {remainingGiftCardBalance > 0 && (
                                <div className="flex justify-between text-blue-650 font-medium">
                                    <span>Remaining Gift Card Balance</span>
                                    <span>₹ {remainingGiftCardBalance.toFixed(2)}</span>
                                </div>
                            )}
                        </>
                    )}
                </div>
                <div className="flex justify-between items-center text-lg font-bold border-t pt-2">
                    <span>Total</span>
                    <span>₹ {finalTotal.toFixed(2)}</span>
                </div>
                <input
                    type="text"
                    placeholder="Add Note (Optional)"
                    className="w-full border border-gray-300 rounded-md p-2 text-sm"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    disabled={checkoutLoading}
                />

                <button
                    onClick={() => onCheckout({
                        coupon: appliedCoupon,
                        giftCard: appliedGiftCard,
                        giftCardCode: appliedGiftCard ? (appliedGiftCard.code || "") : (giftCardInput || ""),
                        discountedTotal: finalTotal,
                        customerNote: note
                    })}
                    disabled={checkoutLoading}
                    className="w-full bg-black text-white text-base font-medium py-3 rounded-lg shadow-md hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {checkoutLoading ? (
                        <>
                            <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            <span>Processing...</span>
                        </>
                    ) : (
                        isNewMember ? "Create Membership & Proceed" : "Proceed to Checkout"
                    )}
                </button>
            </div>

            {isScannerOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-fade-in">
                    <div className="bg-white w-full max-w-md rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] animate-scale-up">
                        {/* Header */}
                        <div className="bg-black text-white px-6 py-4 flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <QrCodeIcon className="h-6 w-6" />
                                <h2 className="text-lg font-bold">Book Scanner (Walk-In)</h2>
                            </div>
                            <button
                                onClick={() => setIsScannerOpen(false)}
                                className="text-gray-400 hover:text-white transition-colors"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>

                        {/* Scanner Content */}
                        <div className="p-6 flex-1 overflow-y-auto space-y-6">
                            <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-xl p-8 bg-gray-50 relative group">
                                <QrCodeIcon className="h-10 w-10 text-gray-700" />
                                <p className="text-sm font-medium text-gray-500 text-center">
                                    Scan a Book QR / Copy ID or type it manually below
                                </p>
                            </div>

                            {/* Manual input / scanner input simulator */}
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleScanSearch(scanQuery);
                                }}
                                className="space-y-4"
                            >
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        autoFocus
                                        value={scanQuery}
                                        onChange={(e) => setScanQuery(e.target.value)}
                                        placeholder="Enter Book Copy ID..."
                                        disabled={scanLoading}
                                        className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-1 focus:ring-black outline-none"
                                    />
                                    <button
                                        type="submit"
                                        disabled={scanLoading || !scanQuery.trim()}
                                        className="px-6 py-2.5 bg-black text-white disabled:opacity-50 text-sm font-semibold rounded-lg transition-colors flex items-center gap-2 min-w-[90px] justify-center"
                                    >
                                        {scanLoading ? (
                                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                        ) : (
                                            "Add"
                                        )}
                                    </button>
                                </div>

                                {scanError && (
                                    <div className="text-sm text-red-655 bg-red-50 px-3 py-2 rounded-lg border border-red-100">
                                        ⚠️ {scanError}
                                    </div>
                                )}
                            </form>

                            {/* List of scanned books in this session */}
                            {scannedList.length > 0 && (
                                <div className="space-y-3 pt-4 border-t border-gray-150">
                                    <div className="text-xs font-bold text-gray-550 uppercase tracking-wider">
                                        Scanned this session ({scannedList.length})
                                    </div>
                                    <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1">
                                        {scannedList.map((item, idx) => (
                                            <div
                                                key={idx}
                                                className="flex items-center gap-3 bg-gray-50 p-2.5 rounded-lg border border-gray-100 animate-slide-in"
                                            >
                                                <img
                                                    src={item.img}
                                                    alt={item.title}
                                                    className="w-10 h-14 object-cover rounded-md shadow-sm"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-semibold text-gray-900 truncate">
                                                        {item.title}
                                                    </div>
                                                    <div className="flex gap-2 items-center mt-1">
                                                        <span className="text-[11px] bg-gray-200/80 text-gray-650 px-1.5 py-0.5 rounded font-mono">
                                                            ID: {item.copyId}
                                                        </span>
                                                        <span className={`text-[11px] px-1.5 py-0.5 rounded font-semibold ${item.status === "Returning"
                                                            ? "bg-amber-105 text-amber-800"
                                                            : "bg-emerald-105 text-emerald-800"
                                                            }`}>
                                                            {item.status}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="bg-gray-50 px-6 py-4 border-t flex justify-end">
                            <button
                                onClick={() => setIsScannerOpen(false)}
                                className="px-5 py-2 border rounded-lg hover:bg-gray-100 transition-colors text-sm font-semibold"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}